import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Colors } from "helpers/Consts";
import { useTheme } from "@mui/system";
import { useFormik } from "formik";
import { IUserAddressAddParams, IUserAddressInfo } from "types/Address";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useQueryAddresses } from "services/api/queries/useQueryAddresses";
import CepTextField from "ui/CepTextField";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { EnderecoViaCep } from "types/EnderecoViaCep";
import { Snackbar } from "@material-ui/core";
import { useMutationEditAddress } from "services/api/mutations/useMutationEditAddress";
import { ICardapioAppState } from "../../redux/RootReducer";
import { UseQueryResult } from "react-query";
import { IShoppingBagData } from "../../types/ShoppingBag";

type Props = {
  open: boolean;
  address: IUserAddressInfo;
  handleClose: () => void;
  shoppingBagQuery: UseQueryResult<IShoppingBagData>
};

function EditAddressDialog({ open, handleClose, address, shoppingBagQuery }: Props) {
  const theme = useTheme();
  const [disabled, setDisabled] = useState<boolean>(true);
  const { mutate: updateAddress } = useMutationEditAddress();
  const [snackBarErroCep, setSnackBarErroCep] = useState<boolean>(false);
  const loginInfo = useSelector((state: ICardapioAppState) => state?.loginInfo);
  const apiAddresses = useQueryAddresses(loginInfo?.id ?? -1);
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: loginInfo?.id ?? -1,
      type: address.type,
      complete_address: address.complete_address,
      description: address.description,
      number: address.number,
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      lat: address.lat,
      lon: address.lon,
      active: address.active,
      additional_info: address.additional_info,
      reference_point: address.reference_point,
    } as IUserAddressAddParams,
    validationSchema: Yup.object().shape({
      description: Yup.string().required("Logradouro é obrigatório."),
      number: Yup.string()
        .min(1, "Número está incompleto")
        .required("Número é obrigatório."),
      neighborhood: Yup.string().required("Bairro é obrigatório."),
      city: Yup.string().required("Cidade é obrigatório."),
      state: Yup.string().required("Estado é obrigatório."),
      postal_code: Yup.string().required("CEP é obrigatório."),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(false);
      formik.values.complete_address = `${formik.values.description}, ${formik.values.number}, ${formik.values.neighborhood}, ${formik.values.city} - ${formik.values.state}`;
      // console.log("values ==>", values);
      const address2: IUserAddressInfo = {
        id: address.id,
        active: true,
        additional_info: values.additional_info,
        city: values.city,
        complete_address: values.complete_address,
        description: values.description,
        lat: values.lat ? values.lat : 0,
        lon: values.lon ? values.lon : 0,
        neighborhood: values.neighborhood,
        number: values.number,
        postal_code: values.postal_code,
        reference_point: values.reference_point,
        state: values.state,
        type: values.type,
        user_id: values.user_id,
        visible: true,
      };

      const request = {...address2, current_shopping_bag_id: shoppingBagQuery.data?.id ?? -1}

      await updateAddress(request);
      apiAddresses.refetch();
      handleClose();
      setTimeout(() => {
        // resetValues();
      }, 2000);
      // resetValues();
      // await addAddress(values);
    },
  });

  function resetValues() {
    formik.values.user_id = loginInfo?.id ?? -1;
    formik.values.type = "H";
    formik.values.complete_address = "";
    formik.values.description = "";
    formik.values.number = "";
    formik.values.neighborhood = "";
    formik.values.city = "";
    formik.values.state = "";
    formik.values.postal_code = "";
    formik.values.lat = 0;
    formik.values.lon = 0;
    formik.values.active = false;
    formik.values.additional_info = "";
    formik.values.reference_point = "";
    setDisabled(true);
    setSnackBarErroCep(false);
  }

  const handleCEP = async () => {
    await fetch(`https://viacep.com.br/ws/${formik.values.postal_code}/json/`)
      .then((res) => res.json())
      .then((endereco: EnderecoViaCep) => {
        formik.setFieldValue("neighborhood", endereco.bairro);
        formik.setFieldValue("city", endereco.localidade);
        formik.setFieldValue("additional_info", endereco.complemento);
        formik.setFieldValue("state", endereco.uf);
        formik.setFieldValue("description", endereco.logradouro);
        formik.setFieldValue("number", "");
        formik.setFieldValue("complete_address", "");
      })
      .then(() => {
        if (formik.values.city !== "" && formik.values.city !== undefined) {
          setDisabled(false);
        } else {
          setDisabled(true);
          resetValues();
          setSnackBarErroCep(true);
        }
      });
  };
  const handleCloseSnackBar = () => {
    setSnackBarErroCep(!snackBarErroCep);
  };
  return (
    <>
      <Snackbar
        open={snackBarErroCep}
        autoHideDuration={2000}
        onClose={handleCloseSnackBar}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity="error"
          sx={{ width: "100%" }}
        >
          Cep inválido.
        </Alert>
      </Snackbar>

      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={() => {
          // resetValues();
          handleClose();
        }}
        maxWidth={"lg"}
      >
        <DialogTitle color={Colors.orangePrimary}>
          <Box display="flex" alignItems="center">
            <Box>
              <IconButton
                color="primary"
                onClick={() => {
                  // resetValues();
                  handleClose();
                }}
              >
                <ArrowBackIosIcon fill={Colors.orangePrimary} />
              </IconButton>
            </Box>
            <Box
              flexGrow={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Editar Endereço
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { md: "600px" },
              height: "100%",
              paddingX: "8px",
            }}
          >
            <form noValidate onSubmit={formik.handleSubmit}>
              <Box>
                <Box sx={{ display: "flex" }}>
                  <CepTextField
                    sx={{
                      width: "30%",
                    }}
                    id="postal_code"
                    label="CEP"
                    variant="standard"
                    type="text"
                    // disabled={true}
                    value={formik.values.postal_code}
                    onChange={formik.handleChange}
                    required
                  />
                  <IconButton size="large" onClick={handleCEP}>
                    <SearchIcon color="primary" />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  id="description"
                  label="Logradouro"
                  placeholder="ex: Rua Barbosa de Freitas"
                  variant="standard"
                  disabled={disabled}
                  type="text"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  required
                />
                <Box
                  display="flex"
                  sx={{
                    marginTop: "8px",
                  }}
                >
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "30%",
                    }}
                    id="number"
                    label="Numero"
                    placeholder="ex: 100"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.number}
                    onChange={formik.handleChange}
                    required
                  />
                  <TextField
                    sx={{
                      marginX: "8px",
                      width: "70%",
                    }}
                    id="additional_info"
                    label="Complemento"
                    placeholder="ex: Bloco A"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.additional_info}
                    onChange={formik.handleChange}
                  />
                </Box>
                <TextField
                  fullWidth
                  id="neighborhood"
                  label="Bairro"
                  placeholder="Bairro"
                  variant="standard"
                  disabled={disabled}
                  type="text"
                  value={formik.values.neighborhood}
                  onChange={formik.handleChange}
                  required
                />
                <Box
                  display="flex"
                  sx={{
                    marginTop: "8px",
                  }}
                >
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "70%",
                    }}
                    id="city"
                    label="Cidade"
                    placeholder="Cidade"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    required
                  />
                  <TextField
                    sx={{
                      marginX: "8px",
                      width: "30%",
                    }}
                    id="state"
                    label="Estado/UF"
                    placeholder="UF"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    required
                  />
                </Box>
                <Box display="flex">
                  <TextField
                    sx={{
                      margin: "0 0 8px 0px",
                      width: "70%",
                    }}
                    id="reference_point"
                    label="Ponto de Referência"
                    placeholder="Ponto de Referência"
                    variant="standard"
                    disabled={disabled}
                    type="text"
                    value={formik.values.reference_point}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>
              {/* {(formik.errors && formik.dirty) && (
            <Alert
              severity="warning"
              sx={{
                display: "flex",
                flexGrow: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontSize="14px" fontWeight={700}>
                Erro no cadastro
              </Typography>
            </Alert>
          )} */}
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Button
                  sx={{
                    width: "100%",
                    color: "white",
                    textTransform: "none",
                  }}
                  type="submit"
                  variant="contained"
                  disabled={
                    !(formik.isValid && formik.dirty) ||
                    formik.values.city === undefined ||
                    formik.values.city === ""
                  }
                >
                  Editar Endereço
                </Button>
              </Box>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EditAddressDialog;
